import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import API from "../api";
import toast from "react-hot-toast";

const statusLabel = { pending:"Pending", cn_wh:"CN WH", in_transit:"In Transit", kh_wh:"KH WH", delivery:"Delivery", signed:"Signed" };
const statusColor = { pending:"#f59e0b", cn_wh:"#3b82f6", in_transit:"#8b5cf6", kh_wh:"#06b6d4", delivery:"#f97316", signed:"#10b981" };
const allStatuses = ["pending","cn_wh","in_transit","kh_wh","delivery","signed"];
const API_URL = "https://api.ctexpresskh.com/api";
const token = () => localStorage.getItem("token");

export default function ShipmentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [ship, setShip] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [showPostPrint, setShowPostPrint] = useState(false);
  const [trackingExists, setTrackingExists] = useState(false);
  const [postPrintStatus, setPostPrintStatus] = useState('kh_wh');
  const [saving, setSaving] = useState(false);
  const [newStatus, setNewStatus] = useState("");
  const [note, setNote] = useState("");
  const [location, setLocation] = useState("");
  const [cs, setCs] = useState({});
  const [custSearch, setCustSearch] = useState("");
  const [custResults, setCustResults] = useState([]);
  const [showCustDrop, setShowCustDrop] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [feePreview, setFeePreview] = useState(null);
  const [manualMode, setManualMode] = useState(false);

  const searchCustomer = async (q) => {
    setCustSearch(q);
    if (!q || q.length < 1) { setCustResults([]); setShowCustDrop(false); return; }
    try {
      const res = await fetch(API_URL + "/customers?search=" + encodeURIComponent(q) + "&limit=20", { headers: { Authorization: "Bearer " + token() } });
      const data = await res.json();
      setCustResults(data.data || []);
      setShowCustDrop((data.data||[]).length > 0);
    } catch {}
  };

  const selectCustomer = (c) => {
    setEditForm(f => ({ ...f, receiver_name: c.name, receiver_phone: c.phone, receiver_address: c.address || "" }));
    setCustSearch(c.customer_code + " - " + c.name);
    setShowCustDrop(false); setCustResults([]);
  };

  const load = () => API.get("/shipments/" + id).then(r => {
    setShip(r.data.data);
    setNewStatus(r.data.data.status);
    setEditForm({
      tracking_number: r.data.data.tracking_number || "",
      sender_name: r.data.data.sender_name || "",
      sender_phone: r.data.data.sender_phone || "",
      receiver_name: r.data.data.receiver_name || "",
      receiver_phone: r.data.data.receiver_phone || "",
      receiver_address: r.data.data.receiver_address || "",
      goods_description: r.data.data.goods_description || "",
      quantity: r.data.data.quantity || 1,
      weight_kg: r.data.data.weight_kg || "",
      length_cm: r.data.data.length_cm || 0,
      width_cm: r.data.data.width_cm || 0,
      height_cm: r.data.data.height_cm || 0,
      shipping_fee: r.data.data.shipping_fee || 0,
      shipping_method: r.data.data.shipping_method || "land",
      manual_cbm: "",
      note: r.data.data.note || "",
    });
  });

  useEffect(() => {
    load();
    fetch(API_URL + "/settings", { headers: { Authorization: "Bearer " + token() } })
      .then(r => r.json()).then(d => { if (d.data) setCs(d.data); });
  }, [id]);

  const updateStatus = async () => {
    try {
      await API.put("/shipments/" + id + "/status", { status: newStatus, note, location });
      toast.success("Status updated!");
      setNote(""); setLocation(""); load();
    } catch (err) { toast.error(err.response?.data?.message || "Error"); }
  };

  const saveEdit = async () => {
    if (trackingExists) { toast.error('Tracking number already exists!'); return; }
    setSaving(true);
    try {
      // Check duplicate tracking if changed
      if (editForm.tracking_number && editForm.tracking_number !== ship.tracking_number) {
        const chk = await fetch(API_URL + "/shipments?search=" + editForm.tracking_number, { headers: { Authorization: "Bearer " + token() } });
        const chkData = await chk.json();
        const exists = (chkData.data || []).find(s => s.tracking_number === editForm.tracking_number && s.id !== id);
        if (exists) { toast.error("Tracking number already exists!"); setSaving(false); return; }
      }
      const res = await fetch(API_URL + "/shipments/" + id, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: "Bearer " + token() },
        body: JSON.stringify(editForm)
      });
      const data = await res.json();
      if (data.success) { toast.success("Shipment updated!"); setEditMode(false); load(); }
      else toast.error(data.message || "Error");
    } catch { toast.error("Server error"); }
    setSaving(false);
  };

  // Live tracking duplicate check
  React.useEffect(() => {
    if (!editForm.tracking_number || editForm.tracking_number === ship?.tracking_number) {
      setTrackingExists(false);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const res = await fetch(API_URL + '/shipments?search=' + editForm.tracking_number, { headers: { Authorization: 'Bearer ' + token() } });
        const data = await res.json();
        const exists = (data.data || []).find(s => s.tracking_number === editForm.tracking_number && s.id !== id);
        setTrackingExists(!!exists);
      } catch {}
    }, 600);
    return () => clearTimeout(timer);
  }, [editForm.tracking_number, ship, id]);

  // Live auto-calculate when dimensions/weight/method changes
  React.useEffect(() => {
    if (manualMode) return;
    const { length_cm: l, width_cm: w, height_cm: h, weight_kg: kg, shipping_method, quantity } = editForm;
    if (!l || !w || !h || !kg || parseFloat(l)===0 || parseFloat(kg)===0) return;
    const doCalc = async () => {
      try {
        const res = await fetch(API_URL + "/rates", { headers: { Authorization: "Bearer " + token() } });
        const data = await res.json();
        const rates = data.data || [];
        const rate = rates.find(r => r.method === (shipping_method || 'land'));
        if (!rate) return;
        const cbm = (parseFloat(l) * parseFloat(w) * parseFloat(h)) / 1000000;
        let type, fee;
        if (cbm >= parseFloat(rate.cbm_threshold)) {
          type = 'CBM';
          fee = Math.max(cbm * parseFloat(rate.rate_per_cbm), (parseFloat(kg) / parseFloat(rate.kg_per_cbm)) * parseFloat(rate.rate_per_cbm));
        } else {
          type = 'EXPRESS';
          fee = Math.max(parseFloat(kg) * parseFloat(rate.rate_per_kg), cbm * parseFloat(rate.rate_per_cbm));
        }
        const qty = parseInt(quantity) || 1;
        const totalFee = (fee * qty).toFixed(2);
        const totalCbm = (cbm * qty).toFixed(4);
        setFeePreview({ cbm: totalCbm, type, fee: totalFee, qty });
        setEditForm(f => ({ ...f, shipping_fee: totalFee }));
      } catch {}
    };
    const timer = setTimeout(doCalc, 500);
    return () => clearTimeout(timer);
  }, [editForm.length_cm, editForm.width_cm, editForm.height_cm, editForm.weight_kg, editForm.shipping_method, editForm.quantity]);

  const calcPreview = async () => {
    const { length_cm: l, width_cm: w, height_cm: h, weight_kg: kg, shipping_method, quantity } = editForm;
    if (!l || !w || !h || !kg) { alert('Enter size and weight first'); return; }
    try {
      const res = await fetch(API_URL + "/rates", { headers: { Authorization: "Bearer " + token() } });
      const data = await res.json();
      const rates = data.data || [];
      const rate = rates.find(r => r.method === (shipping_method || 'land'));
      if (!rate) { alert('Rate not found'); return; }
      const cbm = (parseFloat(l) * parseFloat(w) * parseFloat(h)) / 1000000;
      let type, fee;
      if (cbm >= parseFloat(rate.cbm_threshold)) {
        type = 'CBM';
        fee = Math.max(cbm * parseFloat(rate.rate_per_cbm), (parseFloat(kg) / parseFloat(rate.kg_per_cbm)) * parseFloat(rate.rate_per_cbm));
      } else {
        type = 'EXPRESS';
        fee = Math.max(parseFloat(kg) * parseFloat(rate.rate_per_kg), cbm * parseFloat(rate.rate_per_cbm));
      }
      const qty = parseInt(quantity) || 1;
      const totalFee = (fee * qty).toFixed(2);
      const totalCbm = (cbm * qty).toFixed(4);
      setFeePreview({ cbm: totalCbm, type, fee: totalFee, qty });
      setEditForm(f => ({ ...f, shipping_fee: totalFee }));
    } catch { alert('Error calculating fee'); }
  };
  const confirmPostPrint = async () => {
    try {
      await fetch(API_URL + '/shipments/' + id + '/status', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token() },
        body: JSON.stringify({ status: postPrintStatus, note: 'Updated after printing label' })
      });
      toast.success('Status updated!');
      setShowPostPrint(false);
      load();
    } catch { toast.error('Error updating status'); }
  };
  const printLabel = () => {
    const s = ship;
    const lw = parseFloat(cs.label_width || 80);
    const lh = parseFloat(cs.label_height || 100);
    const date = new Date().toLocaleDateString("en-GB");
    const win = window.open("", "_blank");
    const html = [
      "<!DOCTYPE html><html><head><title>" + s.tracking_number + "</title>",
      "<style>",
      "@page{size:" + lw + "mm " + lh + "mm;margin:0}",
      "*{box-sizing:border-box;margin:0;padding:0}",
      "html,body{width:" + lw + "mm;height:" + lh + "mm;font-family:Arial,sans-serif;overflow:hidden}",
      ".w{width:" + lw + "mm;height:" + lh + "mm;display:flex;flex-direction:column;border:1.5px solid #000}",
      // Header 12mm
      ".hd{display:flex;align-items:center;gap:3px;border-bottom:1.5px solid #000;padding:2px 4px;height:12mm;flex-shrink:0}",
      ".logo{border:1.5px solid #000;padding:2px 3px;text-align:center;font-size:7pt;font-weight:900;line-height:1.1;flex-shrink:0;font-family:serif}",
      ".cn{font-size:9pt;font-weight:900;letter-spacing:0.5px;line-height:1.1}",
      ".ci{font-size:5pt;color:#333;line-height:1.3}",
      // Date 6mm
      ".dt{text-align:center;font-size:8pt;font-weight:900;border-bottom:1px solid #000;flex-shrink:0;height:6mm;display:flex;align-items:center;justify-content:center;background:#f9f9f9}",
      // Info table 20mm
      ".it{width:100%;border-collapse:collapse;flex-shrink:0}",
      ".it td{padding:1.5px 4px;font-size:6pt;border-bottom:1px solid #eee;line-height:1.3}",
      ".it .lb{color:#555;width:60px;font-size:5.5pt;font-weight:600}",
      ".it strong{font-size:6.5pt}",
      // Grid 14mm
      ".gs{border-top:1px solid #000;flex-shrink:0}",
      ".gr{display:flex;padding:1.5px 4px;border-bottom:1px solid #eee;align-items:center}",
      ".gz{font-size:5pt;color:#555;width:65px;flex-shrink:0;font-weight:600}",
      ".gv{font-size:6.5pt;font-weight:700}",
      // Fee 12mm
      ".fe{border-top:1.5px solid #000;text-align:center;flex-shrink:0;height:12mm;display:flex;flex-direction:column;align-items:center;justify-content:center;background:#f9f9f9}",
      ".fz{font-size:5.5pt;color:#555;font-weight:600}",
      ".fv{font-size:13pt;font-weight:900;color:#000}",
      // Tracking 26mm
      ".ts{border-top:1.5px solid #000;text-align:center;flex-shrink:0;height:26mm;display:flex;flex-direction:column;align-items:center;justify-content:center;padding:2px 4px}",
      ".tt{font-size:7.5pt;font-weight:900;margin-bottom:2px}",
      ".tn{font-size:6pt;font-weight:700;letter-spacing:0.5px;margin-top:2px;word-break:break-all}",
      "svg{width:95%;height:16mm;display:block}",
      // Footer 10mm
      ".ft{border-top:1px solid #000;flex:1;display:flex;flex-direction:column;justify-content:center;padding:1.5px 4px;background:#f9f9f9}",
      ".ft div{font-size:5pt;color:#333;line-height:1.4}",
      "@media print{html,body{width:" + lw + "mm;height:" + lh + "mm}}",
      "</style></head><body>",
      "<div class='w'>",
      // Header
      "<div class='hd'>"+(cs.company_logo?"<img src='"+cs.company_logo+"' style='width:28px;height:28px;object-fit:contain;flex-shrink:0'/>":"<div class='logo'>T<br/>F<br/>EX</div>")+
      "<div style='flex:1;margin-left:3px'>",
      "<div class='cn'>" + (cs.company_name||"CT EXPRESS") + "</div>",
      "<div class='ci'>电话/ Tel: " + (cs.company_tel||"") + "</div>",
      "<div class='ci'>地址 /Address: " + (cs.company_address||"") + "</div>",
      "</div></div>",
      // Date
      "<div class='dt'>Date: " + date + "</div>",
      // Receiver info
      "<table class='it'>",
      "<tr><td class='lb'>收件人/ Name:</td><td><strong>" + (s.receiver_name||"") + "</strong></td></tr>",
      "<tr><td class='lb'>电话/ Tel :</td><td>" + (s.receiver_phone||"") + "</td></tr>",
      "<tr><td class='lb'>地址/ Addr:</td><td>" + (s.receiver_address||"") + "</td></tr>",
      "</table>",
      // CBM/Qty/Weight
      "<div class='gs'>",
      "<div class='gr'><span class='gz'>立方/CBM:</span><span class='gv'>" + (parseFloat(s.cbm||0)*(s.quantity||1)).toFixed(4) + " m³</span></div>",
      "<div class='gr'><span class='gz'>数量/Qty:</span><span class='gv'>" + (s.quantity||1) + "</span></div>",
      "<div class='gr'><span class='gz'>毛重/G.W.(KG):</span><span class='gv'>" + (s.weight_kg||0) + "</span></div>",
      "</div>",
      // Fee
      "<div class='fe'>",
      "<div class='fz'>运费/Shipping Cost (Total $):</div>",
      "<div class='fv'>$" + parseFloat((s.shipping_fee||"0").toString().replace(/[^0-9.]/g,"")||0).toFixed(2) + "</div>",
      "</div>",
      // Tracking + barcode
      "<div class='ts'>",
      "<div class='tt'>跟踪号码 /Tracking Number</div>",
      "<svg id='bc'></svg>",
      "<div class='tn'>" + s.tracking_number + "</div>",
      "</div>",
      // Footer
      "<div class='ft'>",
      "<div>- " + (cs.label_footer_1||"សូមពិនិត្យទំនិញ មុននឹងចុះហត្ថលេខាទទួលទំនិញ!") + "</div>",
      "<div>- " + (cs.label_footer_2||"វិវាទនឹងមិនត្រូវបានទទួលយក បន្ទាប់ពីចុះហត្ថលេខារួចហើយ!") + "</div>",
      "</div>",
      "</div>",
      "<script>",
      "function db(el,t){",
      "var n=t.replace(/[^0-9]/g,''),x=2,h=13;",
      "el.setAttribute('viewBox','0 0 220 16');",
      "el.setAttribute('preserveAspectRatio','none');",
      "var bg=document.createElementNS('http://www.w3.org/2000/svg','rect');",
      "bg.setAttribute('width','220');bg.setAttribute('height','16');bg.setAttribute('fill','#fff');el.appendChild(bg);",
      "var e={'0':'3211','1':'2221','2':'2122','3':'1411','4':'1132','5':'1231','6':'1114','7':'1312','8':'1213','9':'3112'};",
      "var bk=true;",
      "for(var i=0;i<Math.min(n.length,26);i++){",
      "var p=(e[n[i]]||'1111').split('');",
      "for(var j=0;j<p.length;j++){",
      "var w=parseInt(p[j])*1.6;",
      "if(bk){var r=document.createElementNS('http://www.w3.org/2000/svg','rect');",
      "r.setAttribute('x',x);r.setAttribute('y','1');",
      "r.setAttribute('width',w);r.setAttribute('height',h);",
      "r.setAttribute('fill','#000');el.appendChild(r);}",
      "x+=w;bk=!bk;}}",
      "}",
      "window.onload=function(){",
      "var el=document.getElementById('bc');",
      "if(el)db(el,'" + s.tracking_number + "');",
      "setTimeout(function(){window.print();},600);window.onafterprint=function(){window.close();};",
      "};",
      "<\/script></body></html>"
    ].join("");
    win.document.write(html);
    win.document.close();
    setTimeout(() => setShowPostPrint(true), 1500);
  };


  if (!ship) return <div style={{ padding: 40, textAlign: "center", color: "#64748b" }}>Loading...</div>;

  const Row = ({ label, value }) => (
    <div style={{ display: "flex", padding: "8px 0", borderBottom: "1px solid #f1f5f9" }}>
      <span style={{ width: 160, color: "#64748b", fontSize: 13, flexShrink: 0 }}>{label}</span>
      <span style={{ fontWeight: 500, color: "#1e293b", fontSize: 13 }}>{value || "-"}</span>
    </div>
  );

  const inp = (label, key, type="text", placeholder="") => (
    <div style={{ marginBottom: 12 }}>
      <label style={{ display: "block", marginBottom: 4, fontSize: 12, fontWeight: 600, color: "#374151" }}>{label}</label>
      <input type={type} value={editForm[key] || ""} onChange={e => setEditForm(f => ({ ...f, [key]: e.target.value }))}
        placeholder={placeholder}
        style={{ width: "100%", padding: "8px 10px", border: "1px solid #d1d5db", borderRadius: 7, fontSize: 13, boxSizing: "border-box" }} />
    </div>
  );

  return (
    <div style={{ padding: 28 }}>
      {/* Header */}
      {/* Header */}
      <div style={{ display:"flex", alignItems:"center", marginBottom:20, position:"relative" }}>
        <button onClick={() => navigate("/shipments")} style={{ background:"#f1f5f9", border:"none", borderRadius:8, padding:"8px 14px", cursor:"pointer", fontSize:13 }}>← Back</button>
        <div style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", gap:8 }}>
          <div style={{ display:"flex", gap:8 }}>
            <button onClick={printLabel}
              style={{ padding:"8px 18px", background:"#059669", color:"#fff", border:"none", borderRadius:8, cursor:"pointer", fontWeight:600, fontSize:13 }}>
              🖨️ Print Label
            </button>
            <button onClick={() => setEditMode(!editMode)}
              style={{ padding:"8px 18px", background: editMode ? "#64748b" : "#3b82f6", color:"#fff", border:"none", borderRadius:8, cursor:"pointer", fontWeight:600, fontSize:13 }}>
              {editMode ? "Cancel Edit" : "✏️ Edit"}
            </button>
          </div>
          <div style={{ display:"flex", alignItems:"center", gap:8 }}>
            <h1 style={{ margin:0, fontSize:20, color:"#1e293b" }}>{ship.tracking_number}</h1>
            <span style={{ background:(statusColor[ship.status]||"#gray")+"20", color:statusColor[ship.status], padding:"4px 14px", borderRadius:20, fontWeight:700, fontSize:12 }}>
              {statusLabel[ship.status]}
            </span>
          </div>
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
        {/* Shipment Info / Edit Form */}
        <div style={{ background: "#fff", borderRadius: 12, padding: 20, boxShadow: "0 1px 4px rgba(0,0,0,0.08)" }}>
          <h3 style={{ margin: "0 0 14px", color: "#f97316", fontSize: 15 }}>{editMode ? "✏️ Edit Shipment" : "Shipment Info"}</h3>
          {editMode ? (
            <div>
              <div style={{ marginBottom: 12 }}>
                <label style={{ display:"block", marginBottom:4, fontSize:12, fontWeight:600, color:"#374151" }}>Tracking Number</label>
                <input value={editForm.tracking_number || ""} onChange={e => setEditForm(f => ({ ...f, tracking_number: e.target.value.toUpperCase() }))}
                  style={{ width:"100%", padding:"8px 10px", border: trackingExists ? "2px solid #dc2626" : "1px solid #f97316", borderRadius:7, fontSize:13, boxSizing:"border-box", fontWeight:700, color:"#f97316" }} />
                {trackingExists && <div style={{ color:"#dc2626", fontSize:11, marginTop:4, fontWeight:600 }}>⚠️ Tracking number already exists!</div>}
              </div>
              {inp("Sender Name", "sender_name")}
              {inp("Sender Phone", "sender_phone")}
              <div style={{ marginBottom: 12, position: "relative" }}>
                <label style={{ display: "block", marginBottom: 4, fontSize: 12, fontWeight: 600, color: "#374151" }}>Search Customer (ID / Name / Phone)</label>
                <input value={custSearch} onChange={e => searchCustomer(e.target.value)}
                  placeholder="Type to search customer..."
                  style={{ width: "100%", padding: "8px 10px", border: "1px solid #d1d5db", borderRadius: 7, fontSize: 13, boxSizing: "border-box" }} />
                {showCustDrop && custResults.length > 0 && (
                  <div style={{ position: "absolute", top: "100%", left: 0, right: 0, background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, boxShadow: "0 4px 12px rgba(0,0,0,0.1)", zIndex: 100, maxHeight: 180, overflowY: "auto" }}>
                    {custResults.map(c => (
                      <div key={c.id} onMouseDown={() => selectCustomer(c)}
                        style={{ padding: "8px 12px", cursor: "pointer", borderBottom: "1px solid #f1f5f9", fontSize: 13 }}
                        onMouseEnter={e => e.currentTarget.style.background="#fff7ed"}
                        onMouseLeave={e => e.currentTarget.style.background="#fff"}>
                        <span style={{ fontWeight: 700, color: "#f97316" }}>{c.customer_code}</span>
                        <span style={{ marginLeft: 8 }}>{c.name}</span>
                        <span style={{ color: "#94a3b8", marginLeft: 8, fontSize: 11 }}>{c.phone}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {inp("Receiver Name", "receiver_name")}
              {inp("Receiver Phone", "receiver_phone")}
              {inp("Receiver Address", "receiver_address")}
              {inp("Description", "goods_description")}
              {inp("Quantity", "quantity", "number")}
              {inp("Weight (kg)", "weight_kg", "number")}
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:8, marginBottom:12 }}>
                <div>
                  <label style={{ display:"block", marginBottom:4, fontSize:12, fontWeight:600, color:"#374151" }}>Length (cm)</label>
                  <input type="number" value={editForm.length_cm || ""} onChange={e => setEditForm(f => ({ ...f, length_cm: e.target.value }))}
                    style={{ width:"100%", padding:"8px 10px", border:"1px solid #d1d5db", borderRadius:7, fontSize:13, boxSizing:"border-box" }} />
                </div>
                <div>
                  <label style={{ display:"block", marginBottom:4, fontSize:12, fontWeight:600, color:"#374151" }}>Width (cm)</label>
                  <input type="number" value={editForm.width_cm || ""} onChange={e => setEditForm(f => ({ ...f, width_cm: e.target.value }))}
                    style={{ width:"100%", padding:"8px 10px", border:"1px solid #d1d5db", borderRadius:7, fontSize:13, boxSizing:"border-box" }} />
                </div>
                <div>
                  <label style={{ display:"block", marginBottom:4, fontSize:12, fontWeight:600, color:"#374151" }}>Height (cm)</label>
                  <input type="number" value={editForm.height_cm || ""} onChange={e => setEditForm(f => ({ ...f, height_cm: e.target.value }))}
                    style={{ width:"100%", padding:"8px 10px", border:"1px solid #d1d5db", borderRadius:7, fontSize:13, boxSizing:"border-box" }} />
                </div>
              </div>
              <div style={{ marginBottom:12 }}>
                <label style={{ display:"block", marginBottom:4, fontSize:12, fontWeight:600, color:"#374151" }}>Shipping Method</label>
                <select value={editForm.shipping_method || "land"} onChange={e => setEditForm(f => ({ ...f, shipping_method: e.target.value }))}
                  style={{ width:"100%", padding:"8px 10px", border:"1px solid #d1d5db", borderRadius:7, fontSize:13 }}>
                  <option value="land">Land</option>
                  <option value="sea">Sea</option>
                  <option value="air">Air</option>
                </select>
              </div>
              <button onClick={calcPreview}
                style={{ width:"100%", padding:"10px", background:"#1e293b", color:"#fff", border:"none", borderRadius:8, cursor:"pointer", fontWeight:700, marginBottom:12 }}>
                Calculate Fee
              </button>
              {feePreview && (
                <div style={{ background:"#fff7ed", border:"1px solid #fed7aa", borderRadius:8, padding:12, marginBottom:12, fontSize:13 }}>
                  <div style={{ display:"flex", justifyContent:"space-between" }}><span style={{ color:"#94a3b8" }}>CBM</span><span>{feePreview.cbm} m³</span></div>
                  <div style={{ display:"flex", justifyContent:"space-between" }}><span style={{ color:"#94a3b8" }}>Type</span><span style={{ color:"#3b82f6", fontWeight:700 }}>{feePreview.type}</span></div>
                  <div style={{ display:"flex", justifyContent:"space-between", marginTop:6 }}><span style={{ fontWeight:700 }}>Fee × {feePreview.qty}</span><span style={{ fontSize:18, fontWeight:900, color:"#f97316" }}>${feePreview.fee}</span></div>
                </div>
              )}
              {/* Mode Toggle */}
              <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:12, background:'#f8fafc', borderRadius:8, padding:'10px 14px' }}>
                <span style={{ fontSize:13, fontWeight:600, color: manualMode ? '#94a3b8' : '#f97316' }}>🔢 Auto</span>
                <div onClick={() => { setManualMode(!manualMode); setFeePreview(null); }}
                  style={{ width:44, height:24, borderRadius:12, background: manualMode ? '#f97316' : '#e2e8f0', cursor:'pointer', position:'relative', transition:'background 0.2s' }}>
                  <div style={{ width:18, height:18, borderRadius:9, background:'#fff', position:'absolute', top:3, left: manualMode ? 23 : 3, transition:'left 0.2s', boxShadow:'0 1px 3px rgba(0,0,0,0.2)' }} />
                </div>
                <span style={{ fontSize:13, fontWeight:600, color: manualMode ? '#f97316' : '#94a3b8' }}>✏️ Manual</span>
              </div>
              {manualMode && (
                <div style={{ background:'#fff7ed', border:'1px solid #fed7aa', borderRadius:8, padding:12, marginBottom:12 }}>
                  <div style={{ fontSize:12, fontWeight:700, color:'#92400e', marginBottom:10 }}>✏️ Manual — enter CBM directly</div>
                  <div>
                    <label style={{ display:"block", marginBottom:4, fontSize:12, fontWeight:600, color:"#374151" }}>Total CBM (m³)</label>
                    <input type="number" value={editForm.manual_cbm || ""} onChange={e => setEditForm(f => ({ ...f, manual_cbm: e.target.value }))}
                      placeholder="e.g. 3.5 total CBM"
                      style={{ width:"100%", padding:"8px 10px", border:"1px solid #fed7aa", borderRadius:7, fontSize:13, boxSizing:"border-box" }} />
                  </div>

                </div>
              )}
              {inp("Shipping Fee ($)", "shipping_fee", "number")}
              {inp("Note", "note")}
              <button onClick={saveEdit} disabled={saving}
                style={{ width: "100%", padding: "10px", background: "#f97316", color: "#fff", border: "none", borderRadius: 8, cursor: "pointer", fontWeight: 700, marginTop: 8, opacity: saving ? 0.7 : 1 }}>
                {saving ? "Saving..." : "💾 Save Changes"}
              </button>
            </div>
          ) : (
            <div>
              <Row label="Tracking" value={ship.tracking_number} />
              <Row label="Sender" value={(ship.sender_name || "") + (ship.sender_phone ? " / " + ship.sender_phone : "")} />
              <Row label="Receiver" value={(ship.receiver_name || "") + " / " + (ship.receiver_phone || "")} />
              <Row label="Address" value={ship.receiver_address} />
              <Row label="Description" value={ship.goods_description} />
              <Row label="Quantity" value={ship.quantity} />
              <Row label="Method" value={(ship.shipping_method || "").toUpperCase()} />
              <Row label="Note" value={ship.note} />
              <Row label="Created" value={new Date(ship.created_at).toLocaleString("en-GB")} />
            </div>
          )}
        </div>

        {/* Fee Calculation */}
        <div style={{ background: "#fff", borderRadius: 12, padding: 20, boxShadow: "0 1px 4px rgba(0,0,0,0.08)" }}>
          <h3 style={{ margin: "0 0 14px", color: "#f97316", fontSize: 15 }}>Fee Calculation</h3>
          <Row label="Weight" value={(ship.weight_kg || 0) + " kg"} />
          <Row label="L × W × H" value={(ship.length_cm || 0) + " × " + (ship.width_cm || 0) + " × " + (ship.height_cm || 0) + " cm"} />
          <Row label="CBM" value={ship.cbm ? (parseFloat(ship.cbm)*(ship.quantity||1)).toFixed(6) : 0} />
          <Row label="Type" value={(ship.shipping_type || "").toUpperCase()} />
          <Row label="Rate/kg" value={"$" + (ship.rate_per_kg || 0)} />
          <Row label="Rate/CBM" value={"$" + (ship.rate_per_cbm || 0)} />
          <div style={{ marginTop: 14, padding: 14, background: "#fff7ed", borderRadius: 10, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontSize: 14, color: "#92400e", fontWeight: 600 }}>Shipping Fee</span>
            <span style={{ fontSize: 24, fontWeight: 900, color: "#f97316" }}>${parseFloat(ship.shipping_fee || 0).toFixed(2)}</span>
          </div>
        </div>

        {/* Update Status */}
        <div style={{ background: "#fff", borderRadius: 12, padding: 20, boxShadow: "0 1px 4px rgba(0,0,0,0.08)" }}>
          <h3 style={{ margin: "0 0 14px", color: "#f97316", fontSize: 15 }}>Update Status</h3>
          <select value={newStatus} onChange={e => setNewStatus(e.target.value)}
            style={{ width: "100%", padding: "9px 12px", border: "1px solid #d1d5db", borderRadius: 8, marginBottom: 10, fontSize: 13 }}>
            {allStatuses.map(s => <option key={s} value={s}>{statusLabel[s]}</option>)}
          </select>
          <input value={location} onChange={e => setLocation(e.target.value)} placeholder="Location (e.g. Guangzhou CN WH)"
            style={{ width: "100%", padding: "9px 12px", border: "1px solid #d1d5db", borderRadius: 8, marginBottom: 10, fontSize: 13, boxSizing: "border-box" }} />
          <input value={note} onChange={e => setNote(e.target.value)} placeholder="Note (optional)"
            style={{ width: "100%", padding: "9px 12px", border: "1px solid #d1d5db", borderRadius: 8, marginBottom: 10, fontSize: 13, boxSizing: "border-box" }} />
          <button onClick={updateStatus}
            style={{ width: "100%", padding: 10, background: "#f97316", color: "#fff", border: "none", borderRadius: 8, cursor: "pointer", fontWeight: 700, fontSize: 13 }}>
            Update Status
          </button>
        </div>

        {/* Status Timeline */}
        <div style={{ background: "#fff", borderRadius: 12, padding: 20, boxShadow: "0 1px 4px rgba(0,0,0,0.08)" }}>
          <h3 style={{ margin: "0 0 14px", color: "#f97316", fontSize: 15 }}>Status Timeline</h3>
          {(ship.history || []).length === 0 ? (
            <div style={{ color: "#94a3b8", textAlign: "center", padding: 20 }}>No history yet</div>
          ) : (ship.history || []).map((h, i) => (
            <div key={i} style={{ display: "flex", gap: 12, marginBottom: 14 }}>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                <div style={{ width: 12, height: 12, borderRadius: "50%", background: statusColor[h.status] || "#94a3b8", marginTop: 3, flexShrink: 0 }} />
                {i < (ship.history||[]).length - 1 && <div style={{ width: 2, flex: 1, background: "#e2e8f0", marginTop: 4 }} />}
              </div>
              <div style={{ flex: 1, paddingBottom: 6 }}>
                <div style={{ fontWeight: 700, fontSize: 13, color: statusColor[h.status] }}>{statusLabel[h.status] || h.status}</div>
                {h.location && <div style={{ fontSize: 12, color: "#64748b" }}>📍 {h.location}</div>}
                {h.note && <div style={{ fontSize: 12, color: "#94a3b8" }}>{h.note}</div>}
                <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>
                  {h.timestamp ? new Date(h.timestamp).toLocaleString("en-GB") : ""}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {showPostPrint && (
        <div style={{ position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.5)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center' }}>
          <div style={{ background:'#fff', borderRadius:16, padding:32, width:360, boxShadow:'0 8px 32px rgba(0,0,0,0.2)' }}>
            <div style={{ fontSize:18, fontWeight:800, marginBottom:16, color:'#1e293b' }}>🖨️ Update Status After Print?</div>
            <div style={{ fontSize:13, color:'#64748b', marginBottom:16 }}>{ship?.tracking_number}</div>
            <div style={{ marginBottom:16 }}>
              <label style={{ fontSize:12, fontWeight:600, color:'#374151', display:'block', marginBottom:6 }}>ផ្លាស់ប្តូរស្ថានភាពទៅ:</label>
              <select value={postPrintStatus} onChange={e => setPostPrintStatus(e.target.value)}
                style={{ width:'100%', padding:'10px 12px', border:'1px solid #e2e8f0', borderRadius:8, fontSize:14 }}>
                <option value="cn_wh">CN Warehouse</option>
                <option value="in_transit">In Transit</option>
                <option value="kh_wh">KH Warehouse</option>
                <option value="delivery">Out for Delivery</option>
                <option value="signed">Delivered</option>
              </select>
            </div>
            <div style={{ display:'flex', gap:10 }}>
              <button onClick={() => setShowPostPrint(false)}
                style={{ flex:1, padding:'10px', background:'#f1f5f9', border:'none', borderRadius:8, cursor:'pointer', fontWeight:600 }}>
                បោះបង់
              </button>
              <button onClick={confirmPostPrint}
                style={{ flex:1, padding:'10px', background:'#f97316', color:'#fff', border:'none', borderRadius:8, cursor:'pointer', fontWeight:700 }}>
                បញ្ជាក់
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
